/**
 * Split that gates the post format filter on every surface that offers it —
 * Insights and the Publish channel page — so one flip releases both.
 *
 * The name is historical: Insights shipped the filter first. Declared here so
 * the gate and each control's alpha/beta badge read the same name; a typo in
 * either would fail silently, one by hiding the filter and the other the badge.
 */
export const POST_TYPE_FILTER_SPLIT = 'insights-post-type-filter'

/**
 * The post formats a filter control can emit, in the order it lists them.
 *
 * The schema accepts more (`event`, `offer`, `short`, `whats_new`,
 * `ghost_post`), but only these three are reachable from a service a control
 * renders for. @see supportedPostTypesForService
 */
export type FilterablePostType = 'post' | 'reel' | 'story'

const FILTERABLE_POST_TYPES: readonly FilterablePostType[] = [
  'post',
  'reel',
  'story',
]

/**
 * Post formats a service can be filtered by. An empty list means the control
 * renders nothing.
 *
 * Only Instagram and Facebook are listed. Every other service either stores no
 * format worth splitting on, or reports formats the filter cannot separate —
 * a Google Business post with nothing stored shows a `whats_new` badge but
 * matches a `post` filter, and a LinkedIn carousel is derived from its media
 * shape rather than stored. @see CORE-6775 for the query semantics.
 *
 * Shared by the Insights and Publish filters so the two surfaces cannot offer
 * the control for different services.
 */
export function supportedPostTypesForService(
  service: string | null | undefined,
): readonly FilterablePostType[] {
  if (service === 'instagram' || service === 'facebook') {
    return FILTERABLE_POST_TYPES
  }
  return []
}

/**
 * Narrows raw `postTypes` URL values to the formats a control can emit.
 *
 * Unknown values are dropped rather than forwarded, because the schema rejects
 * `carousel` and `thread` with an error — a hand-edited or stale URL would
 * otherwise fail the whole query instead of showing an unfiltered page.
 *
 * The result follows {@link FILTERABLE_POST_TYPES} order rather than URL order,
 * so two URLs listing the same formats produce one set of query variables.
 */
export function parsePostTypes(
  raw: string[] | undefined,
): FilterablePostType[] {
  if (!raw?.length) return []
  return FILTERABLE_POST_TYPES.filter((postType) => raw.includes(postType))
}

/**
 * Human-readable label for a post format in a filter control.
 *
 * Singular, to match the format names everywhere else in the product — the
 * composer's Instagram post-type bar and the `PostTypeBadge` on the Insights
 * Top Posts table.
 *
 * Takes any stored format, not just the filterable three, because the Insights
 * export summary labels whatever the export was filtered by. Anything the
 * switch does not name reads as a post, mirroring `generatePostType`.
 */
export function getPostTypeLabel(postType: string): string {
  switch (postType) {
    case 'reel':
      return 'Reel'
    case 'story':
      return 'Story'
    default:
      return 'Post'
  }
}

/** Plural label for a post format, for sentence copy that names a set of posts. */
export function getPostTypeLabelPlural(postType: string): string {
  switch (postType) {
    case 'reel':
      return 'Reels'
    case 'story':
      return 'Stories'
    default:
      return 'Posts'
  }
}
